import { SettingsScope } from '@oneview/constants/lookups';
import type { TabDetails } from '@oneview/constants/tabs';
import { saveSettingsLocal } from '@oneview/redux/settings/settingActions';
import { createQueryFn } from '@oneview/redux/utils';
import type { SettingsOptions, TradeBlotter, UserProfile } from '@oneview/types/settings';
import { isAdvisor } from '@oneview/utils';
import { getHashFromString, showToast } from '@orbis-systems/orbis-react-toolkit';
import { get, pickBy, set } from 'lodash';
import { api } from './baseApi';
import { DataTags } from './dataTags';

const SETTING_PREFIX = 'ria.web.';

function parseSettingsData(data: { [key: string]: string }) {
    const settings: SettingsOptions = {};

    Object.keys(data).forEach((key) => {
        if (key.startsWith(SETTING_PREFIX)) {
            const path = key.replace(SETTING_PREFIX, '');
            set(settings, path, JSON.parse(data[key]!));
        }
    });

    return settings;
}

export const settingApi = api.injectEndpoints({
    endpoints: (build) => ({
        saveSettings: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        newSettings,
                    }: {
                        newSettings: SettingsOptions;
                    },
                    { dispatch, extra: api },
                ) => {
                    const setSettings: Record<string, string> = {};
                    const deleteSettings: Record<string, string> = {};
                    const middlewareSettings: Record<string, string | null> = {};

                    delete newSettings[SettingsScope.Middleware];

                    for (const scope of Object.keys(newSettings)) {
                        const settings = get(newSettings, scope);
                        const isMiddlewareSettings =
                            scope === SettingsScope.TradeBlotter ||
                            scope === SettingsScope.Model ||
                            scope === SettingsScope.Account;

                        if (scope === SettingsScope.Table || isMiddlewareSettings) {
                            for (const setting of Object.keys(settings)) {
                                const path = `${scope}.${setting}`;
                                const value = get(newSettings, path);
                                const key = SETTING_PREFIX + path;
                                if (isMiddlewareSettings) {
                                    middlewareSettings[key] = value === undefined ? null : JSON.stringify(value);
                                } else {
                                    if (value === undefined) {
                                        deleteSettings[key] = '';
                                    } else {
                                        setSettings[key] = JSON.stringify(value);
                                    }
                                }
                            }
                        } else {
                            for (const subScope of Object.keys(settings)) {
                                for (const setting of Object.keys(get(settings, subScope))) {
                                    const path = `${scope}.${subScope}.${setting}`;
                                    const value = get(newSettings, path);
                                    const key = SETTING_PREFIX + path;

                                    if (value === undefined) {
                                        deleteSettings[key] = '';
                                    } else {
                                        setSettings[key] = JSON.stringify(value);
                                    }
                                }
                            }
                        }
                    }

                    dispatch(saveSettingsLocal(newSettings));

                    let orbisData = {};
                    let middlewareData = {};

                    try {
                        if (Object.keys(setSettings).length > 0) {
                            orbisData = (await api.orbisSdk.post('user/preferences', setSettings, { urlQuery: 'set' }))
                                .data;
                        }

                        if (Object.keys(deleteSettings).length > 0) {
                            orbisData = (
                                await api.orbisSdk.post('user/preferences', deleteSettings, { urlQuery: 'delete' })
                            ).data;
                        }

                        if (Object.keys(middlewareSettings).length > 0) {
                            middlewareData = (await api.middlewareSdk.post('preferences', middlewareSettings)).data;
                        }
                    } catch (error) {
                        dispatch(showToast('Failed to save settings'));
                        throw error;
                    }

                    return {
                        ...parseSettingsData(orbisData),
                        ...parseSettingsData(middlewareData),
                    } as SettingsOptions;
                },
            ),
        }),
        restoreSettings: build.mutation<SettingsOptions, void>({
            queryFn: createQueryFn(async (_, { extra: api }) => {
                const settings = await api.orbisSdk.get('user/preferences');
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let branchAttributes: Record<string, any> = {};
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let branchSettings: Record<string, any> = {};

                try {
                    const [middlewareAtributes, middlewareSettings] = await Promise.all([
                        api.middlewareSdk.get('attributes'),
                        api.middlewareSdk.get('preferences'),
                    ]);

                    branchAttributes = middlewareAtributes.data;
                    branchSettings = middlewareSettings.data;
                } catch {
                    // no-op
                }

                const parsedOrbisSettings = parseSettingsData(settings.data || {});
                delete parsedOrbisSettings.tradeBlotter;

                return {
                    ...parsedOrbisSettings,
                    ...parseSettingsData(branchSettings),
                    [SettingsScope.Middleware]: branchAttributes,
                } as SettingsOptions;
            }),
        }),
        resetSettings: build.mutation({
            queryFn: createQueryFn(
                async (_: void, { extra: api, dispatch }) => {
                    const { data } = await api.orbisSdk.get('user/preferences');

                    await api.orbisSdk.post(
                        'user/preferences',
                        pickBy(
                            data,
                            (_, key) =>
                                key.startsWith(SETTING_PREFIX) &&
                                !key.includes(SettingsScope.TradeBlotter) &&
                                !key.includes(SettingsScope.Account),
                        ),
                        { urlQuery: 'delete' },
                    );
                    await dispatch(settingApi.endpoints.restoreSettings.initiate()).unwrap();
                },
                {
                    pendingMessage: 'Resetting all settings…',
                },
            ),
        }),
        saveTabs: build.mutation({
            queryFn: createQueryFn(async (tabs: TabDetails[], { dispatch }) => {
                await dispatch(
                    settingApi.endpoints.saveSettings.initiate({
                        newSettings: {
                            advisor: {
                                poppedOutTabs: {
                                    savedTabs: tabs,
                                },
                            },
                        },
                    }),
                ).unwrap();
            }),
        }),
        saveTradeBlotter: build.mutation({
            queryFn: createQueryFn(async (tradeBlotter: TradeBlotter, { dispatch }) => {
                const blotterId = tradeBlotter.id || getHashFromString(Date.now().toString());
                await dispatch(
                    settingApi.endpoints.saveSettings.initiate({
                        newSettings: {
                            tradeBlotter: {
                                [blotterId]: {
                                    ...tradeBlotter,
                                    id: blotterId,
                                },
                            },
                        },
                        _pendingMessage: 'Saving blotter…',
                    }),
                ).unwrap();
            }),
        }),
        deleteTradeBlotter: build.mutation({
            queryFn: createQueryFn(async (blotterId: string, { dispatch }) => {
                await dispatch(
                    settingApi.endpoints.saveSettings.initiate({
                        newSettings: {
                            tradeBlotter: {
                                [blotterId]: undefined,
                            },
                        },
                        _pendingMessage: 'Deleting blotter…',
                    }),
                ).unwrap();
            }),
        }),
        changeUserProfile: build.mutation({
            queryFn: createQueryFn(async (userProfile: UserProfile, { dispatch }) => {
                await dispatch(
                    settingApi.endpoints.saveSettings.initiate({
                        newSettings: {
                            advisor: {
                                userProfile,
                            },
                        },
                        _pendingMessage: 'Saving user profile…',
                    }),
                ).unwrap();
            }),
        }),

        // Current email, phone, first name and last name must be sent for B2B. Email and phone must be send for B2C.
        setUmaUsersSettings: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        email,
                        phone,
                        first_name,
                        last_name,
                        email_change_code,
                        mfa_enabled,
                        phone_change_code,
                        old_password,
                        new_password,
                        do_not_ask_mfa_again,
                        field,
                        successMessage,
                    }: {
                        email: string;
                        phone: string;
                        first_name?: string;
                        last_name?: string;
                        email_change_code?: string;
                        mfa_enabled?: boolean;
                        phone_change_code?: string;
                        old_password?: string;
                        new_password?: string;
                        do_not_ask_mfa_again?: boolean;
                        field?: string;
                        successMessage?: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        let response;

                        if (isAdvisor()) {
                            response = await api.uma.b2bUserSettings.setUserSettings({
                                email,
                                email_change_code,
                                mfa_enabled,
                                phone,
                                phone_change_code,
                                old_password,
                                new_password,
                                first_name: first_name || '',
                                last_name: last_name || '',
                                do_not_ask_mfa_again,
                                // @ts-expect-error: need to update UmaSetB2BUserSettingsDto
                                field,
                            });
                        } else {
                            response = await api.uma.b2cUserSettings.setUserSettings({
                                email,
                                email_change_code,
                                mfa_enabled,
                                phone,
                                phone_change_code,
                                old_password,
                                new_password,
                                first_name,
                                last_name,
                            });
                        }

                        dispatch(showToast(successMessage || 'Settings updated'));
                        return response.data;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        const errorMessage =
                            error?.response?.data?.errors?.[0]?.description || 'Failed to update user settings';
                        dispatch(showToast(errorMessage));
                        throw error;
                    }
                },
            ),
            invalidatesTags: [DataTags.GetUmaUserInfo, DataTags.GetUmaUserAuthApps],
        }),
        getUmaUserAuthApps: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) => {
                const { data } = await api.uma.authApps.get();

                return data.auth_apps_list;
            }),
            providesTags: [DataTags.GetUmaUserAuthApps],
        }),
        updateUmaUserAuthApp: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        id,
                        name,
                        status,
                        successMessage,
                    }: {
                        id: number;
                        name: string;
                        status: string;
                        successMessage?: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.uma.authApps.update(id, { name, status });
                        dispatch(showToast(successMessage || 'Auth app updated successfully'));
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        const errorMessage =
                            error.response?.data?.errors?.[0]?.description || 'Failed to update auth app';
                        dispatch(showToast(errorMessage));
                        throw error;
                    }
                },
            ),
            invalidatesTags: [DataTags.GetUmaUserAuthApps],
        }),
        generateTOTPRegistrationQr: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        digits,
                        name,
                    }: {
                        digits: number;
                        name: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    let data;

                    try {
                        data = await api.uma.authApps.getRegistrationQr(
                            { digits, name },
                            {
                                responseType: 'blob',
                                transformResponse: (file, headers) => {
                                    return { file, xTotpSecret: headers['x-totp-secret'] };
                                },
                            },
                        );
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        const errorMessage =
                            error?.response?.data?.errors?.[0]?.description ||
                            'Failed to generate TOTP registration QR';
                        dispatch(showToast(errorMessage));
                        throw error;
                    }

                    return data;
                },
            ),
        }),
        finishTOTPRegistration: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        code,
                        name,
                    }: {
                        code: string;
                        name: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    let data;

                    try {
                        data = await api.uma.authApps.finishRegistration({ code, name });
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        const errorMessage =
                            error?.response?.data?.errors?.[0]?.description || 'Failed to finish TOTP registration';
                        dispatch(showToast(errorMessage));
                        throw error;
                    }

                    return data;
                },
            ),
            invalidatesTags: [DataTags.GetUmaUserAuthApps],
        }),
        deleteUmaUserAuthApp: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        ids,
                    }: {
                        ids: number[];
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.uma.authApps.deleteMany({ ids });
                        dispatch(showToast('Auth app deleted successfully'));
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        const errorMessage =
                            error?.response?.data?.errors?.[0]?.description || 'Failed to delete auth app';
                        dispatch(showToast(errorMessage));
                        throw error;
                    }
                },
            ),
            invalidatesTags: [DataTags.GetUmaUserAuthApps],
        }),
    }),
});

export const {
    useRestoreSettingsMutation,
    useSaveSettingsMutation,
    useResetSettingsMutation,
    useSaveTabsMutation,
    useSaveTradeBlotterMutation,
    useDeleteTradeBlotterMutation,
    useChangeUserProfileMutation,
    useSetUmaUsersSettingsMutation,
    useGetUmaUserAuthAppsQuery,
    useUpdateUmaUserAuthAppMutation,
    useGenerateTOTPRegistrationQrMutation,
    useFinishTOTPRegistrationMutation,
    useDeleteUmaUserAuthAppMutation,
} = settingApi;
